import { formatDateTime } from './dateFormat'

export const AVAILABLE_CSV_COLUMNS = [
  { id: 'product_name', label: 'Product Name' },
  { id: 'sku', label: 'Article Code' },
  { id: 'vat', label: 'Vat %' },
  { id: 'dsd', label: 'DSD' },
  { id: 'category', label: 'Category' },
  { id: 'division', label: 'Division' },
  { id: 'description', label: 'Description' },
  { id: 'brand', label: 'Brand' },
  { id: 'company_rate', label: 'Company Rate' },
  { id: 'tp_map', label: 'TP/MAP' },
  { id: 'tax_category', label: 'Tax Category' },
  { id: 'unit_of_measure', label: 'Unit of Measure' },
  { id: 'pack_size', label: 'Pack Size' },
  { id: 'pack_type', label: 'Pack Type' },
  { id: 'logo_image', label: 'Logo Image URL' },
  { id: 'portrait_image', label: 'Portrait Image URL' },
  { id: 'banner_image', label: 'Banner Image URL' },
  { id: 'additional_images', label: 'Additional Images' },
  { id: 'status', label: 'Status' },
  { id: 'is_purchasable', label: 'Is purchasable' },
  { id: 'created_at', label: 'Created At' },
]

export const PRODUCT_SEARCH_DEBOUNCE_MS = 500

export function parseProductManageSearchParams(searchParams) {
  const q = searchParams.get('q') || ''
  const page = Math.max(0, (parseInt(searchParams.get('page') || '1', 10) || 1) - 1)
  const parsedLimit = parseInt(searchParams.get('limit') || '10', 10) || 10
  const limit = [5, 10, 25, 50].includes(parsedLimit) ? parsedLimit : 10
  return { q, page, limit }
}

export function buildProductManageSearchParams({ q, page, limit }) {
  const params = new URLSearchParams()
  const trimmed = (q || '').trim()
  if (trimmed) params.set('q', trimmed)
  if (page > 0) params.set('page', String(page + 1))
  if (limit !== 10) params.set('limit', String(limit))
  return params
}

export function getProductManageReturnPath(searchParams) {
  const query =
    typeof searchParams?.toString === 'function'
      ? searchParams.toString()
      : ''
  return query ? `/products/manage?${query}` : '/products/manage'
}

export function productManageHasExplicitParams(searchParams) {
  return (
    searchParams.has('q') ||
    searchParams.has('page') ||
    searchParams.has('limit')
  )
}

/**
 * Map UI list filters → GET my-uploads query params.
 * `page` in filters is 0-based; API page is 1-based.
 */
export function toProductApiParams({ q, page, limit }) {
  const params = {
    page: (Number.isFinite(page) ? page : 0) + 1,
    limit: limit ?? 10,
  }
  const trimmed = (q || '').trim()
  if (trimmed) {
    params.search = trimmed
  }
  return params
}

export function buildProductDetailPath(id, returnPath) {
  const params = new URLSearchParams()
  if (returnPath) params.set('return', returnPath)
  const query = params.toString()
  return query ? `/products/${id}?${query}` : `/products/${id}`
}

export function resolveProductDetailReturnPath(searchParams, locationState) {
  const fromQuery = searchParams?.get?.('return')
  if (fromQuery && fromQuery.startsWith('/')) return fromQuery
  if (locationState?.returnTo && String(locationState.returnTo).startsWith('/')) {
    return locationState.returnTo
  }
  return null
}

export const normalizeProducts = (productsData) => productsData || []

export const getStatusColor = (status) => {
  switch (status) {
    case 'approved':
      return 'success'
    case 'pending':
      return 'warning'
    case 'rejected':
      return 'error'
    default:
      return 'default'
  }
}

/** Form / table display for Admin Product VAT% (preserves numeric zero). */
export function formatVatPercentForForm(vat) {
  if (vat === null || vat === undefined || vat === '') return ''
  const n = Number(vat)
  if (!Number.isFinite(n)) return String(vat)
  return String(n)
}

/** Table / detail display; shows em dash when VAT% is absent. */
export function formatVatPercentDisplay(vat) {
  if (vat === null || vat === undefined || vat === '') return '—'
  const n = Number(vat)
  if (!Number.isFinite(n)) return String(vat)
  return String(n)
}

/**
 * Aligns with backend parseAdminProductVat:
 * absent / blank → absent; 0–100 with ≤2 decimals → value; else invalid.
 */
export function parseVatPercentInput(raw) {
  if (raw === undefined || raw === null) return { kind: 'absent' }
  if (typeof raw === 'string' && raw.trim() === '') return { kind: 'absent' }

  const trimmed = String(raw).trim()
  if (!/^-?\d+(\.\d+)?$/.test(trimmed)) {
    return {
      kind: 'invalid',
      reason: 'VAT% must be a number between 0 and 100 with at most 2 decimal places',
    }
  }
  const decimalPart = trimmed.includes('.') ? trimmed.split('.')[1] : ''
  if (decimalPart.length > 2) {
    return {
      kind: 'invalid',
      reason: 'VAT% must have at most 2 decimal places',
    }
  }
  const value = Number(trimmed)
  if (!Number.isFinite(value)) {
    return {
      kind: 'invalid',
      reason: 'VAT% must be a finite number between 0 and 100',
    }
  }
  if (value < 0 || value > 100) {
    return {
      kind: 'invalid',
      reason: 'VAT% must be between 0 and 100 inclusive',
    }
  }
  return { kind: 'value', value }
}

/** Display DSD as Yes/No (false when missing). */
export function formatDsdDisplay(dsd) {
  return dsd === true ? 'Yes' : 'No'
}

export const buildEditFormData = (product) => ({
  product_name: product.product_name,
  description: product.description || '',
  sku: product.sku,
  source: product.source || 'shwapno',
  category_id: product.category_id || '',
  division_id: product.division_id || '',
  brand: product.brand || '',
  company_rate: product.company_rate || '',
  tp_map: product.tp_map || '',
  tax_category: product.tax_category || '',
  unit_of_measure: product.unit_of_measure || '',
  vat: formatVatPercentForForm(product.vat),
  dsd: product.dsd === true,
  pack_size: product.pack_size ?? '',
  pack_type: product.pack_type || '',
  image_logo_url: product.image_url?.logo || '',
  image_portrait_url: product.image_url?.portrait || '',
  image_banner_url: product.image_url?.banner || '',
  additional_images: product.image_url?.additional_images
    ? JSON.stringify(product.image_url.additional_images)
    : '',
  is_active: product.is_active !== false,
  is_purchasable: product.is_purchasable !== false,
})

const CSV_COLUMN_MAP = {
  product_name: (p) => p.product_name,
  sku: (p) => p.sku,
  vat: (p) => (p.vat === null || p.vat === undefined || p.vat === '' ? '' : String(p.vat)),
  dsd: (p) => (p.dsd === true ? '1' : '0'),
  category: (p) => p.category_name || '',
  division: (p) => p.division_name || p.division_slug || '',
  description: (p) => p.description || '',
  barcode: (p) => p.barcode || '',
  brand: (p) => p.brand || '',
  company_rate: (p) => p.company_rate || '',
  tp_map: (p) => p.tp_map || '',
  tax_category: (p) => p.tax_category || '',
  unit_of_measure: (p) => p.unit_of_measure || '',
  pack_size: (p) => (p.pack_size != null && p.pack_size !== '' ? p.pack_size : ''),
  pack_type: (p) => p.pack_type || '',
  logo_image: (p) => p.image_url?.logo || '',
  portrait_image: (p) => p.image_url?.portrait || '',
  banner_image: (p) => p.image_url?.banner || '',
  additional_images: (p) =>
    p.image_url?.additional_images && p.image_url.additional_images.length > 0
      ? JSON.stringify(p.image_url.additional_images)
      : '',
  status: (p) => p.status || '',
  is_purchasable: (p) => (p.is_purchasable !== false ? 'Yes' : 'No'),
  created_at: (p) => (p.created_at ? formatDateTime(p.created_at, '') : ''),
}

export const buildCsvContent = (productsToExport, selectedColumns) => {
  const headers = selectedColumns.map((colId) => {
    const col = AVAILABLE_CSV_COLUMNS.find((c) => c.id === colId)
    return col ? col.label : colId
  })

  const rows = productsToExport.map((p) =>
    selectedColumns.map((colId) => (CSV_COLUMN_MAP[colId] ? CSV_COLUMN_MAP[colId](p) : '')),
  )

  return [
    headers.join(','),
    ...rows.map((r) =>
      r
        .map((cell, index) => {
          const cellValue = cell !== null && cell !== undefined ? String(cell) : ''
          if (selectedColumns[index] === 'additional_images' && cellValue.startsWith('[')) {
            return `"${cellValue.replace(/"/g, '""')}"`
          }
          return `"${cellValue.replace(/"/g, '""')}"`
        })
        .join(','),
    ),
  ].join('\n')
}

export const downloadCsvFile = (csv, filename) => {
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' })
  const url = window.URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = filename
  document.body.appendChild(a)
  a.click()
  window.URL.revokeObjectURL(url)
  document.body.removeChild(a)
}

export const PACK_SIZE_FIELD_ERROR = 'Pack Size must be a whole number greater than 0'

export const PACK_SIZE_FIELD_HELPER =
  'Optional — units per pack (whole number greater than 0)'

/** @returns {string|null} Field error message, or null when valid / empty. */
export function getPackSizeFieldError(raw) {
  if (raw === undefined || raw === null || String(raw).trim() === '') {
    return null
  }
  const trimmed = String(raw).trim()
  const parsed = Number(trimmed)
  if (!Number.isFinite(parsed) || !Number.isInteger(parsed) || parsed <= 0) {
    return PACK_SIZE_FIELD_ERROR
  }
  return null
}

export function getEditProductFieldErrors(editFormData) {
  const errors = {}
  const packSizeError = getPackSizeFieldError(editFormData?.pack_size)
  if (packSizeError) {
    errors.pack_size = packSizeError
  }
  return errors
}

export function isPackSizeApiErrorMessage(message) {
  if (!message) return false
  return String(message).includes(PACK_SIZE_FIELD_ERROR)
}

export function resolvePackSizeForPayload(raw) {
  const fieldError = getPackSizeFieldError(raw)
  if (fieldError) {
    return null
  }
  if (raw === undefined || raw === null || String(raw).trim() === '') {
    return null
  }
  return Number(String(raw).trim())
}

export const parseAdditionalImages = (additionalImages) => {
  if (!additionalImages) return null
  try {
    if (additionalImages.trim().startsWith('[')) {
      return JSON.parse(additionalImages)
    }
    return additionalImages
      .split(',')
      .map((url) => url.trim())
      .filter((url) => url.length > 0)
  } catch {
    return additionalImages
      .split(',')
      .map((url) => url.trim())
      .filter((url) => url.length > 0)
  }
}

export const buildProductUpdatePayload = (editFormData) => {
  const imageData = {}
  if (editFormData.image_logo_url) imageData.logo = editFormData.image_logo_url
  if (editFormData.image_portrait_url) imageData.portrait = editFormData.image_portrait_url
  if (editFormData.image_banner_url) imageData.banner = editFormData.image_banner_url
  const additional = parseAdditionalImages(editFormData.additional_images)
  if (additional) imageData.additional_images = additional

  const payload = {
    product_name: editFormData.product_name,
    description: editFormData.description,
    sku: editFormData.source === 'external' ? '' : editFormData.sku,
    source: editFormData.source,
    category_id: editFormData.category_id || null,
    division_id: editFormData.division_id || null,
    brand: editFormData.brand || null,
    company_rate: editFormData.company_rate || null,
    tp_map: editFormData.tp_map?.trim() ? editFormData.tp_map.trim() : null,
    tax_category: editFormData.tax_category || null,
    unit_of_measure: editFormData.unit_of_measure || null,
    pack_size: resolvePackSizeForPayload(editFormData.pack_size),
    pack_type: editFormData.pack_type?.trim()
      ? editFormData.pack_type.trim().toLowerCase()
      : null,
    image_url: Object.keys(imageData).length > 0 ? imageData : null,
  }
  // Blank VAT% is omitted so the backend preserves the stored value.
  const vatParsed = parseVatPercentInput(editFormData.vat)
  if (vatParsed.kind === 'invalid') {
    const err = new Error(vatParsed.reason)
    err.code = 'INVALID_VAT'
    throw err
  }
  if (vatParsed.kind === 'value') {
    payload.vat = vatParsed.value
  }
  // External products are never purchasable; omit so edits don't attempt to toggle it.
  if (editFormData.source !== 'external') {
    payload.is_purchasable = editFormData.is_purchasable !== false
    // DSD only for shwapno; always send boolean when editing so switch changes persist.
    payload.dsd = editFormData.dsd === true
  }
  return payload
}
